import { capitalize } from 'utils/string';
import { Row, Col } from 'components/grid';
import { ExpensesType, ButtonColors } from 'types';
import { useCreateMutation } from 'store/api/payout';
import { Dialog, DialogType } from 'components/dialog';
import { Button, ButtonTypes } from 'components/button';
import { PaymentsForm } from 'components/payments-form';
import { handleFormSubmitWithFiles } from 'utils/helpers';
import { useCategoriesQuery } from 'store/api/accounting';
import { useAlert, isMobile, handleMutation } from 'hooks';
import { Messages, acceptPdfAndImageFileTypes } from 'config';
import { DropZoneContent } from 'components/drop-zone-content';
import { NewEntityButton } from 'components/new-entity-button';
import { useDetailsQuery, useCashManagerQuery } from 'store/api/event';
import {
  SelectField,
  CheckboxField,
  RadioFieldGroup,
  FileDropzoneField,
} from 'components/fields';

import {
  FormValues,
  ExpenseTypeCashStatus,
  CreateBonusAdvanceDialogProps,
} from './types';
import {
  getInitialValues,
  getValidationSchema,
  hideReceiptFieldTypes,
  expenseTypeToPayoutMapping,
} from './config';

export const CreateBonusAdvanceDialog = ({
  eventId,
  trigger,
  employeeId,
  isAdvances,
  withReceipt,
  customTitle,
  isWideDialog,
  customEvents,
  onSuccessSubmit,
  customTypeValues,
  hideCashFromEvent,
  isOnlyCashLocation,
  isDisableEvent = true,
  customEmployeeOptions,
  showSelectExpensesType,
}: CreateBonusAdvanceDialogProps) => {
  const mobile = isMobile();
  const [create, mutation] = useCreateMutation();
  const { handleError, showSuccessAlert } = useAlert();
  const { data, isFetching } = useDetailsQuery(String(eventId), {
    skip: !eventId || !!customEmployeeOptions,
  });
  const { data: cashData, isFetching: isCashFetching } = useCashManagerQuery(
    String(eventId),
    { skip: !eventId }
  );

  const { data: categories, isFetching: isCategoryFetching } =
    useCategoriesQuery();

  const categoryOptions = categories?.map((value) => ({
    label: value?.name,
    value: value?.id?.toString(),
  }));

  const title = isAdvances ? 'advance' : 'bonus';
  handleMutation(mutation);

  const typeValues =
    customTypeValues ||
    Object.values(ExpensesType).filter((value) => value !== ExpensesType.Misc);

  const employeeOptions =
    customEmployeeOptions ||
    [data?.leader, ...(data?.teamMembers || [])].map((value) => ({
      label: String(value?.name),
      value: String(value?.employee?.id),
    }));

  const onSubmit = async (values: FormValues, close: () => void) => {
    const defaultType = isAdvances ? ExpensesType.Advance : ExpensesType.Bonus;

    const cashStatus = values.isCashFromEvent
      ? ExpenseTypeCashStatus.Prepaid
      : ExpenseTypeCashStatus.Regular;

    const type =
      expenseTypeToPayoutMapping[values.type || defaultType]?.[cashStatus];

    const response = await create({
      ...values,
      type,
      amount: +values.amount,
      employeeId: +values.employeeId,
      categoryId: values?.categoryId ? +values.categoryId : undefined,
    }).unwrap();

    if (response.id) {
      onSuccessSubmit?.({ ...values, id: response.id });
      close();
      showSuccessAlert(`${type} ${Messages.ADDED_SUCCESSFULLY}`);
    }
  };

  return (
    <Dialog
      title={customTitle || `Create ${title}`}
      width={isWideDialog ? (mobile ? '90vw' : '600px') : undefined}
      trigger={
        trigger || <NewEntityButton text={customTitle || `Add ${title}`} />
      }
    >
      {(close, _, ButtonWrap) => (
        <PaymentsForm<FormValues>
          isDialog
          isTopChildren
          hideBackButton
          customEvents={customEvents}
          isDisableEvent={isDisableEvent}
          isDisabledAmount={isCashFetching}
          initialValues={getInitialValues({
            eventId,
            employeeId,
            isOnlyCashLocation,
          })}
          validationSchema={getValidationSchema({
            withReceipt,
            showSelectExpensesType,
            cashFlowBalance: cashData?.balance,
          })}
          footerButtons={({ dirty, isSubmitting }) => (
            <ButtonWrap type={DialogType.Plain}>
              <Button
                text="Cancel"
                onClick={close}
                fullWidth={mobile}
                colorType={ButtonColors.LightBlue}
              />
              <Button
                text="Add"
                fullWidth={mobile}
                type={ButtonTypes.Submit}
                disabled={!dirty || isSubmitting}
              />
            </ButtonWrap>
          )}
          onSubmit={
            withReceipt
              ? handleFormSubmitWithFiles({
                  data: {},
                  handleError,
                  onSubmit: (values) => onSubmit(values, close),
                  files: [
                    {
                      formFieldName: 'receipt',
                      dataFieldName: 'receiptUrl',
                      requestFieldName: 'receiptUrl',
                    },
                  ],
                })
              : (values) => onSubmit(values, close)
          }
        >
          {({ values }) => {
            const isShowReceipt =
              withReceipt &&
              values?.type &&
              !hideReceiptFieldTypes.includes(values?.type as ExpensesType);

            return (
              <>
                {showSelectExpensesType && (
                  <Row>
                    <Col>
                      <RadioFieldGroup
                        name="type"
                        transparent
                        padding="0"
                        label="Select Type"
                        items={typeValues.map((value) => ({
                          value,
                          label: capitalize(value),
                        }))}
                      />
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <SelectField
                      onlyValue
                      label="Employee"
                      name="employeeId"
                      options={employeeOptions}
                      isDisabled={isFetching || !!customEmployeeOptions}
                    />
                  </Col>
                </Row>

                {!hideCashFromEvent && (
                  <Row>
                    <Col>
                      <CheckboxField
                        name="isCashFromEvent"
                        label="Cash From Location"
                        disabled={isOnlyCashLocation}
                      />
                    </Col>
                  </Row>
                )}

                {isShowReceipt && (
                  <>
                    <Row>
                      <Col>
                        <FileDropzoneField
                          errorMessage
                          name="receipt"
                          label="Upload receipt"
                          accept={acceptPdfAndImageFileTypes}
                        >
                          <DropZoneContent file={values.receipt} />
                        </FileDropzoneField>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <SelectField
                          onlyValue
                          name="categoryId"
                          label="Select category"
                          options={categoryOptions}
                          placeholder="Select category"
                          isDisabled={isCategoryFetching}
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </>
            );
          }}
        </PaymentsForm>
      )}
    </Dialog>
  );
};
